import { colors, mediaQueries, theme } from '@sato/components';
import { merge } from 'lodash';
import { VictoryStyleObject, VictoryThemeDefinition } from 'victory';

export const baseLabelStyles = {
  fontFamily: theme.fontFamily.sans[0],
  fill: colors['black-text'],
} satisfies VictoryStyleObject;

export const warmWaterColor = colors.brand.primary.main;

export const coldWaterColor = colors.brand.interactive.main;

export const isMobileChartSize =
  window.innerWidth < mediaQueries.breakpoints.sm;

/** Fixed chart size for both mobile and desktop clients that allows
 * scaling styles consistently on all screen sizes. NOTE: isn't the
 * actual element size on screen but rather the relative viewport
 * size for SVGs */
const CHART_SIZE = 400;
const CHART_HEIGHT = isMobileChartSize ? CHART_SIZE : CHART_SIZE / 2.25;

export const axisStyles = {
  tickLabels: {
    ...baseLabelStyles,
    fontSize: isMobileChartSize ? 14 : 6,
    fill: colors['black-medium-text'],
  },
  axisLabel: {
    ...baseLabelStyles,
    angle: 0,
    textAnchor: 'start',
    fontSize: isMobileChartSize ? 16 : 6,
    fontWeight: 'bold',
    textTransform: 'capitalize',
    fill: colors['black-medium-text'],
  },
} satisfies NonNullable<VictoryThemeDefinition['axis']>['style'];

/** Utility for calculating pixel size relative chart size */
export const chartRem = (rem: number) => rem * CHART_HEIGHT * 0.01;

// Theming is bit complicated, some of the styling has to be given directly to components
export const waterUsageChartTheme = {
  chart: {
    width: CHART_SIZE,
    height: CHART_HEIGHT,
    padding: {
      top: chartRem(10),
      left: chartRem(8),
      right: 0,
      bottom: chartRem(1.5),
    },
    domainPadding: { x: chartRem(5) },
  },
  tooltip: {
    cornerRadius: chartRem(2),
    pointerLength: chartRem(2),
    flyoutWidth: isMobileChartSize ? 220 : 90,
    flyoutHeight: isMobileChartSize ? 130 : 55,
    flyoutPadding: {
      top: chartRem(2),
      left: chartRem(3),
      right: chartRem(3),
      bottom: chartRem(3),
    },
    flyoutStyle: {
      strokeWidth: chartRem(0.2),
      stroke: colors['black-medium'],
      fill: colors['white'],
    },
  },
  axis: {
    style: axisStyles,
  },
  independentAxis: {
    width: CHART_SIZE,
    height: CHART_HEIGHT,
    offsetY: chartRem(1),
    style: {
      ...axisStyles,
      ticks: {
        fill: 'transparent',
        size: chartRem(1),
        stroke: colors.black,
        strokeWidth: 1,
      },
      axis: {
        stroke: colors.black,
        strokeWidth: 1,
      },
      grid: { stroke: 'transparent' },
    },
  },
  dependentAxis: {
    width: CHART_SIZE,
    height: CHART_HEIGHT,
    offsetY: 2,
    style: merge(axisStyles, {
      grid: {
        stroke: '#CCCCCC',
        strokeWidth: 1,
        strokeDasharray: `${chartRem(3)} ${chartRem(1)}`,
      },
      axis: { stroke: 'transparent' },
    } satisfies NonNullable<VictoryThemeDefinition['dependentAxis']>['style']),
  },
  legend: {
    orientation: 'horizontal',
    gutter: chartRem(6),
    style: {
      labels: {
        ...baseLabelStyles,
        fontSize: isMobileChartSize ? chartRem(4) : chartRem(3.5),
      },
    },
  },
  stack: {
    colorScale: [warmWaterColor, coldWaterColor],
  },
  bar: {
    style: {
      data: { width: isMobileChartSize ? 22 : 8 },
    },
  },
} satisfies VictoryThemeDefinition;
